import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { useState, useEffect } from 'react';
import { 
  Save, 
  TestTube, 
  Mail, 
  CheckCircle, 
  XCircle, 
  Loader2,
  HelpCircle,
  Send
} from 'lucide-react';
import { useToast } from '@/hooks/useToast';

interface EmailConfig {
  emailHost: string;
  emailPort: number;
  emailSecure: boolean;
  emailUser: string;
  emailPassword: string;
  emailFromName: string;
  emailFromAddress: string;
}

interface PresetConfig {
  name: string;
  host: string;
  port: number;
  secure: boolean;
}

const PRESET_CONFIGS: PresetConfig[] = [
  { name: 'Gmail', host: 'smtp.gmail.com', port: 587, secure: false },
  { name: 'Outlook', host: 'smtp.office365.com', port: 587, secure: false },
  { name: 'SendGrid', host: 'smtp.sendgrid.net', port: 587, secure: false },
  { name: 'Custom', host: '', port: 587, secure: false },
];

export default function EmailConfigurationSettings() {
  const { toast } = useToast();
  
  const [config, setConfig] = useState<EmailConfig>({
    emailHost: '',
    emailPort: 587,
    emailSecure: false,
    emailUser: '',
    emailPassword: '',
    emailFromName: '',
    emailFromAddress: '',
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [showTestDialog, setShowTestDialog] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState('Custom');

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/email/config', {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setConfig(data);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Error al cargar la configuración de email",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePresetChange = (presetName: string) => {
    setSelectedPreset(presetName);
    const preset = PRESET_CONFIGS.find(p => p.name === presetName);
    if (preset && presetName !== 'Custom') {
      setConfig(prev => ({
        ...prev,
        emailHost: preset.host,
        emailPort: preset.port,
        emailSecure: preset.secure,
      }));
    }
  };

  const handleSaveConfig = async () => {
    if (!config.emailHost || !config.emailUser || !config.emailPassword || !config.emailFromName || !config.emailFromAddress) {
      toast({
        title: "Error",
        description: "Todos los campos son requeridos",
        variant: "destructive"
      });
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(config.emailUser)) {
      toast({
        title: "Error",
        description: "El email del usuario no es válido",
        variant: "destructive"
      });
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(config.emailFromAddress)) {
      toast({
        title: "Error",
        description: "El email del remitente no es válido",
        variant: "destructive"
      });
      return;
    }

    if (config.emailPort < 1 || config.emailPort > 65535) {
      toast({
        title: "Error",
        description: "El puerto debe estar entre 1 y 65535",
        variant: "destructive"
      });
      return;
    }

    try {
      setSaving(true);
      const response = await fetch('/api/email/config', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(config)
      });

      const data = await response.json();

      if (response.ok) {
        toast({
          title: "Éxito",
          description: "Configuración guardada correctamente",
        });
      } else {
        toast({
          title: "Error",
          description: data.error || "Error al guardar la configuración",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Error al guardar la configuración",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      setTesting(true);
      const response = await fetch('/api/email/test-connection', {
        method: 'POST',
        credentials: 'include'
      });

      const result = await response.json();

      if (result.success) {
        toast({
          title: "Conexión exitosa",
          description: result.message,
        });
      } else {
        toast({
          title: "Error de conexión",
          description: result.error || result.message,
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Error al probar la conexión",
        variant: "destructive"
      });
    } finally {
      setTesting(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(testEmail)) {
      toast({
        title: "Error",
        description: "Por favor ingresa un email válido",
        variant: "destructive"
      });
      return;
    }

    try {
      setSendingTest(true);
      const response = await fetch('/api/email/send-test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ recipientEmail: testEmail })
      });

      const result = await response.json();

      if (result.success) {
        toast({
          title: "Email enviado",
          description: result.message,
        });
        setShowTestDialog(false);
        setTestEmail('');
      } else {
        toast({
          title: "Error al enviar",
          description: result.error || result.message,
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Error al enviar el email de prueba",
        variant: "destructive"
      });
    } finally {
      setSendingTest(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="h-5 w-5" />
          Configuración de Email
        </CardTitle>
        <CardDescription>
          Configura el servidor SMTP para enviar emails desde la aplicación
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Preset Selector */}
        <div className="space-y-2">
          <Label htmlFor="preset">Proveedor de Email</Label>
          <select
            id="preset"
            value={selectedPreset}
            onChange={(e) => handlePresetChange(e.target.value)}
            className="w-full px-3 py-2 border rounded-md"
          >
            {PRESET_CONFIGS.map(preset => (
              <option key={preset.name} value={preset.name}>
                {preset.name}
              </option>
            ))}
          </select>
        </div>

        {/* SMTP Configuration */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="emailHost">
              Servidor SMTP *
            </Label>
            <Input
              id="emailHost"
              type="text"
              placeholder="smtp.example.com"
              value={config.emailHost}
              onChange={(e) => setConfig({ ...config, emailHost: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="emailPort">
              Puerto *
            </Label>
            <Input
              id="emailPort"
              type="number"
              placeholder="587"
              value={config.emailPort}
              onChange={(e) => setConfig({ ...config, emailPort: parseInt(e.target.value) || 587 })}
            />
            <p className="text-xs text-gray-500">
              587 (STARTTLS), 465 (SSL), o 25 (sin cifrado)
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="emailUser">
              Usuario / Email *
            </Label>
            <Input
              id="emailUser"
              type="email"
              placeholder="tu-email@example.com"
              value={config.emailUser}
              onChange={(e) => setConfig({ ...config, emailUser: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="emailPassword">
              Contraseña *
            </Label>
            <Input
              id="emailPassword"
              type="password"
              placeholder="••••••••"
              value={config.emailPassword}
              onChange={(e) => setConfig({ ...config, emailPassword: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="emailFromName">
              Nombre del Remitente *
            </Label>
            <Input
              id="emailFromName"
              type="text"
              placeholder="QCampusOne"
              value={config.emailFromName}
              onChange={(e) => setConfig({ ...config, emailFromName: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="emailFromAddress">
              Email del Remitente *
            </Label>
            <Input
              id="emailFromAddress"
              type="email"
              placeholder="noreply@example.com"
              value={config.emailFromAddress}
              onChange={(e) => setConfig({ ...config, emailFromAddress: e.target.value })}
            />
          </div>
        </div>

        {/* SSL/TLS Toggle */}
        <div className="flex items-center justify-between p-4 border rounded-lg">
          <div className="space-y-0.5 flex-1">
            <Label>Conexión Segura (SSL/TLS)</Label>
            <p className="text-sm text-gray-500">
              Se detecta automáticamente según el puerto (465 = SSL, 587 = STARTTLS)
            </p>
          </div>
          <input
            type="checkbox"
            checked={config.emailSecure}
            onChange={(e) => setConfig({ ...config, emailSecure: e.target.checked })}
            className="h-4 w-4 text-blue-600 rounded"
          />
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3 pt-4">
          <Button
            onClick={handleSaveConfig}
            disabled={saving}
            className="flex items-center gap-2"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            Guardar Configuración
          </Button>

          <Button
            onClick={handleTestConnection}
            disabled={testing}
            variant="outline"
            className="flex items-center gap-2"
          >
            {testing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <TestTube className="h-4 w-4" />
            )}
            Probar Conexión
          </Button>

          <Dialog open={showTestDialog} onOpenChange={setShowTestDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" className="flex items-center gap-2">
                <Send className="h-4 w-4" />
                Enviar Email de Prueba
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Enviar Email de Prueba</DialogTitle>
                <DialogDescription>
                  Ingresa un email para recibir un mensaje de prueba y verificar que la configuración funciona correctamente.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="testEmail">Email de Destino</Label>
                  <Input
                    id="testEmail"
                    type="email"
                    placeholder="destinatario@example.com"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setShowTestDialog(false)}
                  disabled={sendingTest}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleSendTestEmail}
                  disabled={sendingTest}
                  className="flex items-center gap-2"
                >
                  {sendingTest ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4" />
                      Enviar Prueba
                    </>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Help Section */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
          <div className="flex items-start gap-3">
            <HelpCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="space-y-3 text-sm">
              <p className="font-medium text-blue-900">Ayuda de Configuración</p>
              
              <div className="space-y-2">
                <p className="font-medium text-blue-800">📧 Gmail con Autenticación de 2 Factores:</p>
                <ol className="list-decimal list-inside space-y-1 text-blue-700 ml-2">
                  <li>Ve a <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="underline font-medium hover:text-blue-900">Google App Passwords</a></li>
                  <li>Inicia sesión con tu cuenta de Gmail</li>
                  <li>Crea una nueva contraseña de aplicación para "Mail"</li>
                  <li>Copia la contraseña generada (16 caracteres) y úsala aquí</li>
                  <li><strong className="text-red-600">⚠️ NO uses tu contraseña normal de Gmail</strong></li>
                </ol>
              </div>

              <div className="space-y-1">
                <p className="font-medium text-blue-800">🔧 Puertos SMTP:</p>
                <ul className="list-disc list-inside space-y-1 text-blue-700 ml-2">
                  <li><strong>Puerto 465:</strong> Conexión SSL directa (más seguro)</li>
                  <li><strong>Puerto 587:</strong> Conexión STARTTLS (estándar moderno, recomendado)</li>
                  <li><strong>Puerto 25:</strong> Sin cifrado (no recomendado)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
